import type { GatewayBrowserClient } from "../gateway.ts";
import type { CronJob, CronRunLogEntry, CronStatus } from "../types.ts";
import type { CronFormState } from "../ui-types.ts";
import { toNumber } from "../format.ts";

export type CronState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  cronLoading: boolean;
  cronJobs: CronJob[];
  cronStatus: CronStatus | null;
  cronError: string | null;
  cronForm: CronFormState;
  cronRunsJobId: string | null;
  cronRuns: CronRunLogEntry[];
  cronBusy: boolean;
  cronEditingJobId: string | null;
};

export async function loadCronStatus(state: CronState) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<CronStatus>("cron.status", {});
    state.cronStatus = res;
  } catch (err) {
    state.cronError = String(err);
  }
}

export async function loadCronJobs(state: CronState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.cronLoading) {
    return;
  }
  state.cronLoading = true;
  state.cronError = null;
  try {
    const res = await state.client.request<{ jobs?: Array<CronJob> }>("cron.list", {
      includeDisabled: true,
    });
    state.cronJobs = Array.isArray(res.jobs) ? res.jobs : [];
  } catch (err) {
    state.cronError = String(err);
  } finally {
    state.cronLoading = false;
  }
}

export function buildCronSchedule(form: CronFormState) {
  if (form.scheduleKind === "at") {
    const ms = Date.parse(form.scheduleAt);
    if (!Number.isFinite(ms)) {
      throw new Error("Invalid run time.");
    }
    return { kind: "at" as const, at: new Date(ms).toISOString() };
  }
  if (form.scheduleKind === "every") {
    const amount = toNumber(form.everyAmount, 0);
    if (amount <= 0) {
      throw new Error("Invalid interval amount.");
    }
    const unit = form.everyUnit;
    const mult = unit === "minutes" ? 60_000 : unit === "hours" ? 3_600_000 : 86_400_000;
    return { kind: "every" as const, everyMs: amount * mult };
  }
  const expr = form.cronExpr.trim();
  if (!expr) {
    throw new Error("Cron expression required.");
  }
  return { kind: "cron" as const, expr, tz: form.cronTz.trim() || undefined };
}

export function buildCronPayload(form: CronFormState) {
  if (form.payloadKind === "systemEvent") {
    const text = form.payloadText.trim();
    if (!text) {
      throw new Error("System event text required.");
    }
    return { kind: "systemEvent" as const, text };
  }
  const message = form.payloadText.trim();
  if (!message) {
    throw new Error("Agent message required.");
  }
  const payload: {
    kind: "agentTurn";
    message: string;
    timeoutSeconds?: number;
  } = { kind: "agentTurn", message };
  const timeoutSeconds = toNumber(form.timeoutSeconds, 0);
  if (timeoutSeconds > 0) {
    payload.timeoutSeconds = timeoutSeconds;
  }
  return payload;
}

export async function addCronJob(state: CronState) {
  if (!state.client || !state.connected || state.cronBusy) {
    return;
  }
  state.cronBusy = true;
  state.cronError = null;
  try {
    const schedule = buildCronSchedule(state.cronForm);
    const payload = buildCronPayload(state.cronForm);
    const delivery =
      state.cronForm.sessionTarget === "isolated" &&
      state.cronForm.payloadKind === "agentTurn" &&
      state.cronForm.deliveryMode
        ? {
            mode: state.cronForm.deliveryMode === "announce" ? "announce" : "none",
            channel: state.cronForm.deliveryChannel.trim() || "last",
            to: state.cronForm.deliveryTo.trim() || undefined,
          }
        : undefined;
    const agentId = state.cronForm.agentId.trim();
    const job = {
      name: state.cronForm.name.trim(),
      description: state.cronForm.description.trim() || undefined,
      agentId: agentId || undefined,
      enabled: state.cronForm.enabled,
      schedule,
      sessionTarget: state.cronForm.sessionTarget,
      wakeMode: state.cronForm.wakeMode,
      payload,
      delivery,
    };
    if (!job.name) {
      throw new Error("Name required.");
    }
    await state.client.request("cron.add", job);
    state.cronForm = {
      ...state.cronForm,
      name: "",
      description: "",
      payloadText: "",
    };
    await loadCronJobs(state);
    await loadCronStatus(state);
  } catch (err) {
    state.cronError = String(err);
  } finally {
    state.cronBusy = false;
  }
}

export async function toggleCronJob(state: CronState, job: CronJob, enabled: boolean) {
  if (!state.client || !state.connected || state.cronBusy) {
    return;
  }
  state.cronBusy = true;
  state.cronError = null;
  try {
    await state.client.request("cron.update", { id: job.id, patch: { enabled } });
    await loadCronJobs(state);
    await loadCronStatus(state);
  } catch (err) {
    state.cronError = String(err);
  } finally {
    state.cronBusy = false;
  }
}

export async function runCronJob(state: CronState, job: CronJob) {
  if (!state.client || !state.connected || state.cronBusy) {
    return;
  }
  state.cronBusy = true;
  state.cronError = null;
  try {
    await state.client.request("cron.run", { id: job.id, mode: "force" });
    await loadCronRuns(state, job.id);
  } catch (err) {
    state.cronError = String(err);
  } finally {
    state.cronBusy = false;
  }
}

export async function removeCronJob(state: CronState, job: CronJob) {
  if (!state.client || !state.connected || state.cronBusy) {
    return;
  }
  state.cronBusy = true;
  state.cronError = null;
  try {
    await state.client.request("cron.remove", { id: job.id });
    if (state.cronRunsJobId === job.id) {
      state.cronRunsJobId = null;
      state.cronRuns = [];
    }
    await loadCronJobs(state);
    await loadCronStatus(state);
  } catch (err) {
    state.cronError = String(err);
  } finally {
    state.cronBusy = false;
  }
}

export async function loadCronRuns(state: CronState, jobId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<{ entries?: Array<CronRunLogEntry> }>("cron.runs", {
      id: jobId,
      limit: 50,
    });
    state.cronRunsJobId = jobId;
    state.cronRuns = Array.isArray(res.entries) ? res.entries : [];
  } catch (err) {
    state.cronError = String(err);
  }
}

/**
 * Convert an existing job into the form so the user can edit it.
 */
export function loadCronJobForEdit(state: CronState, job: CronJob) {
  const form = cronJobToFormState(job);
  if (!form) {
    state.cronError = `Unsupported cron schedule kind: ${(job.schedule as { kind?: string }).kind ?? "?"}`;
    return;
  }
  state.cronEditingJobId = job.id;
  state.cronForm = form as unknown as CronFormState;
  state.cronError = null;
}

/**
 * Cancel the current edit and reset to defaults.
 */
export function cancelCronEdit(state: CronState) {
  state.cronEditingJobId = null;
  state.cronForm = { ...state.cronForm, name: "", description: "", payloadText: "" };
}

/**
 * Push the current form back to the gateway as a patch on the editing job.
 */
export async function updateCronJobFromForm(state: CronState) {
  if (!state.client || !state.connected || state.cronBusy) {
    return;
  }
  const jobId = state.cronEditingJobId;
  if (!jobId) {
    state.cronError = "No job selected for editing.";
    return;
  }
  state.cronBusy = true;
  state.cronError = null;
  try {
    const schedule = buildCronSchedule(state.cronForm);
    const payload = buildCronPayload(state.cronForm);
    const delivery =
      state.cronForm.sessionTarget === "isolated" &&
      state.cronForm.payloadKind === "agentTurn" &&
      state.cronForm.deliveryMode
        ? {
            mode: state.cronForm.deliveryMode === "announce" ? "announce" : "none",
            channel: state.cronForm.deliveryChannel.trim() || "last",
            to: state.cronForm.deliveryTo.trim() || undefined,
          }
        : undefined;
    const agentId = state.cronForm.agentId.trim();
    const patch = {
      name: state.cronForm.name.trim() || undefined,
      description: state.cronForm.description.trim() || undefined,
      agentId: agentId || null,
      enabled: state.cronForm.enabled,
      schedule,
      sessionTarget: state.cronForm.sessionTarget,
      wakeMode: state.cronForm.wakeMode,
      payload,
      delivery,
    };
    if (!patch.name) {
      throw new Error("Name required.");
    }
    await state.client.request("cron.update", { id: jobId, patch });
    state.cronEditingJobId = null;
    state.cronForm = {
      ...state.cronForm,
      name: "",
      description: "",
      payloadText: "",
    };
    await loadCronJobs(state);
    await loadCronStatus(state);
    await loadCronRuns(state, jobId);
  } catch (err) {
    state.cronError = String(err);
  } finally {
    state.cronBusy = false;
  }
}

/**
 * Pure helper used by the controller and the view to materialise a job
 * into a CronFormState. Mirrors the format written by addCronJob.
 */
export function cronJobToFormState(job: CronJob) {
  const form: Record<string, unknown> = {
    name: job.name ?? "",
    description: job.description ?? "",
    agentId: job.agentId ?? "",
    enabled: job.enabled ?? true,
    sessionTarget: job.sessionTarget,
    wakeMode: job.wakeMode,
  };
  if (job.schedule.kind === "at") {
    form.scheduleKind = "at";
    const ms = Date.parse(job.schedule.at);
    if (Number.isFinite(ms)) {
      const d = new Date(ms);
      const pad = (n: number) => String(n).padStart(2, "0");
      form.scheduleAt = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } else {
      form.scheduleAt = "";
    }
  } else if (job.schedule.kind === "every") {
    form.scheduleKind = "every";
    const everyMs = Number(job.schedule.everyMs);
    if (Number.isFinite(everyMs) && everyMs > 0) {
      if (everyMs % 86_400_000 === 0) {
        form.everyAmount = String(everyMs / 86_400_000);
        form.everyUnit = "days";
      } else if (everyMs % 3_600_000 === 0) {
        form.everyAmount = String(everyMs / 3_600_000);
        form.everyUnit = "hours";
      } else if (everyMs % 60_000 === 0) {
        form.everyAmount = String(everyMs / 60_000);
        form.everyUnit = "minutes";
      } else {
        form.everyAmount = String(Math.round(everyMs / 60_000));
        form.everyUnit = "minutes";
      }
    }
  } else if (job.schedule.kind === "cron") {
    form.scheduleKind = "cron";
    form.cronExpr = job.schedule.expr ?? "";
    form.cronTz = job.schedule.tz ?? "";
  } else {
    return null;
  }
  if (job.payload.kind === "systemEvent") {
    form.payloadKind = "systemEvent";
    form.payloadText = job.payload.text ?? "";
  } else {
    form.payloadKind = "agentTurn";
    form.payloadText = job.payload.message ?? "";
    if (typeof job.payload.timeoutSeconds === "number") {
      form.timeoutSeconds = String(job.payload.timeoutSeconds);
    } else {
      form.timeoutSeconds = "";
    }
  }
  const delivery = job.delivery;
  if (delivery) {
    form.deliveryMode = delivery.mode === "announce" ? "announce" : "none";
    form.deliveryChannel = delivery.channel ?? "last";
    form.deliveryTo = delivery.to ?? "";
  }
  return form;
}
